import { Box } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  TextField,
} from '@verantos-dev/shared-components';

import { useRenameAnalyticalDatasetDialog } from '../hooks/useRenameAnalyticalDatasetDialog';

const NAME_MAX_CHARACTERS = 60;

interface RenameAnalyticalDatasetDialogProps {
  projectId: string;
  analyticalDatasetProfileId: string;
  currentName: string;
  onClose: () => void;
}

/**
 * Inline rename dialog opened from an analytical-dataset row. Lives here (not
 * reused from feature-analytical-datasets) to avoid a dependency cycle. Reuses the
 * `analyticalDatasetsList.renameModal.*` message ids — registered app-wide via
 * `analyticalDatasetsMessageKeysEn`. A duplicate name (409) surfaces as the
 * submitting error.
 */
export const RenameAnalyticalDatasetDialog = ({
  projectId,
  analyticalDatasetProfileId,
  currentName,
  onClose,
}: RenameAnalyticalDatasetDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError,
    isDuplicateName,
    isValidToSubmit,
  } = useRenameAnalyticalDatasetDialog({
    projectId,
    analyticalDatasetProfileId,
    currentName,
    onClose,
  });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'analyticalDatasetsList.renameModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'analyticalDatasetsList.renameModal.renameButton' }),
    submittingCancelButtonText: formatMessage({
      id: 'analyticalDatasetsList.renameModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({
      id: 'analyticalDatasetsList.renameModal.submitting',
    }),
    successCloseButtonText: formatMessage({ id: 'analyticalDatasetsList.renameModal.closeButton' }),
    submittingErrorTitle: formatMessage({
      id: 'analyticalDatasetsList.renameModal.submittingError',
    }),
    successTitle: formatMessage({ id: 'analyticalDatasetsList.renameModal.success.title' }),
    successText: formatMessage({ id: 'analyticalDatasetsList.renameModal.success.text' }, { name }),
  };

  return (
    <ApiWorkflowDialog
      id="renameAnalyticalDatasetDialog"
      title={formatMessage({ id: 'analyticalDatasetsList.renameModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <TextField
          id="rename-analytical-dataset-name-input"
          displayLabel={formatMessage({ id: 'analyticalDatasetsList.renameModal.name.label' })}
          value={name}
          placeholder={formatMessage({ id: 'analyticalDatasetsList.renameModal.name.placeholder' })}
          onChange={onChangeName}
          maxCharacters={NAME_MAX_CHARACTERS}
          maxCharacterErrorMsg={formatMessage({
            id: 'analyticalDatasetsList.renameModal.maxCharacterErrorMsg',
          })}
          error={isDuplicateName}
          helperText={
            isDuplicateName
              ? formatMessage({ id: 'analyticalDatasetsList.duplicateNameError' })
              : undefined
          }
          displayNumCharacter
          width="auto"
          beepMaxCharacters
        />
      </Box>
    </ApiWorkflowDialog>
  );
};

export default RenameAnalyticalDatasetDialog;
